import { Chip, Input, Select } from '@emailforge/ui';
import { useState } from 'react';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import { type SendEventRow,useListSendsQuery, useSendsStatsQuery } from '@/features/sends/sendsApi';
import { t } from '@/lib/i18n';

export function SendsScreen() {
  const { data: sends, isLoading } = useListSendsQuery({ limit: 100 }, { pollingInterval: 5000 });
  const { data: stats } = useSendsStatsQuery();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [query, setQuery] = useState('');

  const filtered = (sends ?? [])
    .filter((s) => statusFilter === 'all' || s.status === statusFilter)
    .filter((s) => {
      if (!query) return true;
      return (
        s.templateSlug?.toLowerCase().includes(query.toLowerCase()) ||
        s.recipientHash.includes(query) ||
        s.providerMessageId?.toLowerCase().includes(query.toLowerCase())
      );
    });

  return (
    <>
      <TopBar
        crumbs={[t('nav.sends')]}
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Chip variant="live">tailing · live
            </Chip>
          </div>
        }
      />
      <div style={{ flex: 1, padding: 24, overflowY: 'auto', background: 'var(--paper-2)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Delivered · 7d', value: stats?.delivered ?? '—', color: 'var(--spark)' },
            { label: 'Opened · 7d', value: stats?.opened ?? '—', color: 'var(--ink)' },
            { label: 'Clicked · 7d', value: stats?.clicked ?? '—', color: 'var(--flame)' },
            { label: 'Bounced · 7d', value: stats?.bounced ?? '—', color: 'var(--err)' },
            { label: 'Deferred · 7d', value: stats?.deferred ?? '—', color: 'var(--warn)' },
          ].map((k) => (
            <div key={k.label} className="mf-card" style={{ padding: 14 }}>
              <div
                className="mono"
                style={{ fontSize: 10, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.06 }}
              >
                {k.label}
              </div>
              <div className="display" style={{ fontSize: 26, color: k.color, fontWeight: 500, marginTop: 6 }}>
                {k.value}
              </div>
            </div>
          ))}
        </div>

        <div
          className="mf-card"
          style={{ padding: 10, display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}
        >
          <div style={{ position: 'relative', flex: 1 }}>
            <Input mono
              
              placeholder="recipient.hash:* OR template.slug:*"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ paddingLeft: 28 }} />
            <div style={{ position: 'absolute', left: 8, top: 7, color: 'var(--ink-3)' }}>
              <Icon.search size={13} />
            </div>
          </div>
          <Select  value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="queued">Queued</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="opened">Opened</option>
            <option value="clicked">Clicked</option>
            <option value="bounced">Bounced</option>
            <option value="failed">Failed</option>
            <option value="deferred">Deferred</option>
          </Select>
        </div>

        <div className="mf-card">
          <table className="mf-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Template</th>
                <th>Recipient</th>
                <th>Provider msg</th>
                <th>Latency</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id}>
                  <td>
                    <StatusChip status={s.status} />
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {s.templateSlug ?? '—'}
                  </td>
                  <td className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                    {s.recipientHash}
                  </td>
                  <td className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                    {s.providerMessageId?.slice(0, 20) ?? '—'}
                  </td>
                  <td className="mono" style={{ fontSize: 11.5 }}>
                    {s.latencyMs ? `${s.latencyMs}ms` : '—'}
                  </td>
                  <td className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                    {new Date(s.occurredAt).toLocaleTimeString()}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
                    No send events yet. Publish a template and hit <code className="mono">POST /api/send/:id</code> to see events here.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function StatusChip({ status }: { status: SendEventRow['status'] }) {
  return <Chip variant={colorClass(status)}>{status}</Chip>;
}

function colorClass(status: string): 'live' | 'draft' | 'err' | 'flame' {
  if (['delivered', 'opened', 'sent'].includes(status)) return 'live';
  if (['clicked'].includes(status)) return 'flame';
  if (['bounced', 'failed', 'complained'].includes(status)) return 'err';
  return 'draft';
}
